import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';

const themeOptions = {
  light: {
    palette: { 
      mode: 'light', 
      primary: { main: '#1976d2' }, 
      background: { default: '#fafbfc', paper: '#ffffff' }, 
      taskHover: '#f5f7fa',
      divider: 'rgba(0, 0, 0, 0.06)',
    },
    typography: { 
      fontFamily: "'Inter', 'Manrope', -apple-system, BlinkMacSystemFont, sans-serif",
      h4: { fontWeight: 600, letterSpacing: '-0.01em' },
      h5: { fontWeight: 600, letterSpacing: '-0.01em' },
      h6: { fontWeight: 600, letterSpacing: '-0.01em' },
      subtitle1: { fontWeight: 500, letterSpacing: '-0.005em' },
      subtitle2: { fontWeight: 400, letterSpacing: '0' },
      body1: { fontWeight: 400 },
      body2: { fontWeight: 400, color: 'rgba(0, 0, 0, 0.65)' },
      caption: { fontWeight: 400, color: 'rgba(0, 0, 0, 0.55)' },
    },
  },
  dark: {
    palette: { 
      mode: 'dark', 
      primary: { main: '#90caf9' }, 
      background: { default: '#181a1b', paper: '#1e2124' }, 
      taskHover: '#23272e',
      divider: 'rgba(255, 255, 255, 0.08)',
    },
    typography: { 
      fontFamily: "'Inter', 'Manrope', -apple-system, BlinkMacSystemFont, sans-serif",
      h4: { fontWeight: 600, letterSpacing: '-0.01em' },
      h5: { fontWeight: 600, letterSpacing: '-0.01em' },
      h6: { fontWeight: 600, letterSpacing: '-0.01em' },
      subtitle1: { fontWeight: 500, letterSpacing: '-0.005em' },
      subtitle2: { fontWeight: 400, letterSpacing: '0' },
      body1: { fontWeight: 400 },
      body2: { fontWeight: 400, color: 'rgba(255, 255, 255, 0.65)' },
      caption: { fontWeight: 400, color: 'rgba(255, 255, 255, 0.55)' },
    },
  },
  blue: {
    palette: {
      mode: 'light',
      primary: { main: '#1976d2' },
      secondary: { main: '#64b5f6' },
      background: { default: '#f5f7fa', paper: '#ffffff' },
      text: { primary: '#183153', secondary: '#3a4a6b' },
      taskHover: '#e8f0f8',
      divider: 'rgba(0, 0, 0, 0.06)',
    },
    typography: { 
      fontFamily: "'Inter', 'Manrope', -apple-system, BlinkMacSystemFont, sans-serif",
      h4: { fontWeight: 600, letterSpacing: '-0.01em' },
      h5: { fontWeight: 600, letterSpacing: '-0.01em' },
      h6: { fontWeight: 600, letterSpacing: '-0.01em' },
      subtitle1: { fontWeight: 500, letterSpacing: '-0.005em' },
      subtitle2: { fontWeight: 400, letterSpacing: '0' },
      body1: { fontWeight: 400 },
      body2: { fontWeight: 400, color: 'rgba(24, 49, 83, 0.65)' },
      caption: { fontWeight: 400, color: 'rgba(24, 49, 83, 0.55)' },
    },
  },
  green: {
    palette: { mode: 'light', primary: { main: '#388e3c' }, secondary: { main: '#81c784' }, background: { default: '#f7f8fa' }, taskHover: '#e6f4ea' },
    typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" },
  },
  purple: {
    palette: { mode: 'light', primary: { main: '#7b1fa2' }, secondary: { main: '#ba68c8' }, background: { default: '#f7f8fa' }, taskHover: '#f3e6fa' },
    typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" },
  },
  orange: {
    palette: { mode: 'light', primary: { main: '#f57c00' }, secondary: { main: '#ffb74d' }, background: { default: '#f7f8fa' }, taskHover: '#fff3e0' },
    typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" },
  },
  pink: {
    palette: { mode: 'light', primary: { main: '#d81b60' }, secondary: { main: '#f06292' }, background: { default: '#f7f8fa' }, taskHover: '#fde6ef' },
    typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" },
  },
  ikb: {
    palette: {
      mode: 'light',
      primary: { main: '#002fa7' },
      secondary: { main: '#0053d6' },
      background: { default: '#f4f7fd', paper: '#fafdff' },
      text: { primary: '#00204a', secondary: '#3a4a6b' },
      taskHover: '#e0eafd',
    },
    typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" },
  },
  lilac: {
    palette: {
      mode: 'light',
      primary: { main: '#b57edc' },
      secondary: { main: '#e0bbff' },
      background: { default: '#faf7fd', paper: '#f9f3ff' },
      text: { primary: '#5a3a6b', secondary: '#8e6fa7' },
      taskHover: '#f1e6fa',
    },
    typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" },
  },
};

const ThemeContext = createContext();

export const useThemeContext = () => useContext(ThemeContext);

export const CustomThemeProvider = ({ children }) => {
  const [themeName, setThemeName] = useState(() => localStorage.getItem('theme') || 'ikb');

  useEffect(() => {
    localStorage.setItem('theme', themeName);
  }, [themeName]);

  const theme = useMemo(() => {
    const baseTheme = createTheme(themeOptions[themeName] || themeOptions.ikb);
    // Add custom spacing scale (8/12/16/24px)
    baseTheme.spacing = (factor) => `${0.5 * factor * 8}px`;
    return baseTheme;
  }, [themeName]);

  return (
    <ThemeContext.Provider value={{ themeName, setThemeName }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
}; 